/**
 * 端末内 LLM（BROWSER_LLM）の抽出結果を検証する（FR-006 / MA-1）。
 *
 * <p>バックエンド `PayslipValidator` の移植。差引＝総支給−控除合計、社会保険料の検出、
 * 支給内訳（基本給・残業・手当が揃う場合のみ）を確認し、不一致は警告として加え、
 * 信頼度を減点する（0〜1 にクランプ）。</p>
 */

/** 検証対象（端末内 LLM の結果＋初期信頼度）。 */
export interface SlipValidationInput {
  grossSalary?: number;
  netSalary?: number;
  basicSalary?: number;
  overtime?: number;
  allowances?: number;
  healthInsurance?: number;
  employmentInsurance?: number;
  nationalPension?: number;
  incomeTax?: number;
  localTax?: number;
  definedContribution?: number;
  confidence?: number;
}

export interface SlipValidationResult {
  warnings: string[];
  confidence: number;
}

/** 不一致 1 件あたりの信頼度減点。 */
const PENALTY = 0.1;

/** 抽出結果を検証し、警告と検証後の信頼度を返す。 */
export function validateSlip(input: SlipValidationInput): SlipValidationResult {
  const warnings: string[] = [];
  let confidence = input.confidence ?? 0;
  const gross = input.grossSalary;

  // 支給内訳（基本給 + 残業代 + 手当）と総支給額の一致（3 項目が揃う場合のみ）
  if (
    isDefined(gross) &&
    isDefined(input.basicSalary) &&
    isDefined(input.overtime) &&
    isDefined(input.allowances)
  ) {
    const breakdown = input.basicSalary + input.overtime + input.allowances;
    if (breakdown > 0 && breakdown !== gross) {
      warnings.push('支給内訳の合計が総支給額と一致しません');
      confidence -= PENALTY;
    }
  }

  // 差引支給額 = 総支給額 − 控除合計
  if (isDefined(gross) && isDefined(input.netSalary)) {
    const deductions = sum(
      input.healthInsurance,
      input.employmentInsurance,
      input.nationalPension,
      input.incomeTax,
      input.localTax,
      input.definedContribution,
    );
    if (gross - deductions !== input.netSalary) {
      warnings.push('差引支給額が総支給額−控除合計と一致しません');
      confidence -= PENALTY;
    }
  }

  // 社会保険料が全く検出されない
  if (
    isDefined(gross) &&
    gross > 0 &&
    isNullOrZero(input.healthInsurance) &&
    isNullOrZero(input.employmentInsurance) &&
    isNullOrZero(input.nationalPension)
  ) {
    warnings.push('社会保険料が検出されませんでした');
    confidence -= PENALTY;
  }

  return { warnings, confidence: Math.max(0, Math.min(1, confidence)) };
}

function isDefined(value: number | undefined): value is number {
  return value !== undefined && value !== null;
}

function isNullOrZero(value: number | undefined): boolean {
  return value === undefined || value === null || value === 0;
}

function sum(...values: Array<number | undefined>): number {
  return values.reduce<number>((total, value) => total + (value ?? 0), 0);
}
